import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { InputSwitch } from "primereact/inputswitch";
import { Button, Container } from "reactstrap";
import { Save } from "lucide-react";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import { interviewEmailFields, readInterviewEmailSettings } from "utils/interviewEmailSettings";
import moment from "moment";

const errorText = error => {
  if (error.response?.status === 404) return "The Interview Email Settings API was not found. Please check that the endpoint is available on the server.";
  if (error.response?.status === 403) return "You do not have permission to manage this company's interview email settings.";
  if (error.response?.status === 401) return "Your session has expired. Please sign in again.";
  if (typeof error.response?.data === "string" && /<\s*(?:!doctype|html|body)\b/i.test(error.response.data)) return "The server returned an unexpected response. Please try again or contact support.";
  const flatten = (value, prefix = "") => typeof value === "object" && value !== null
    ? Object.entries(value).flatMap(([key, item]) => flatten(item, `${prefix}${key}: `))
    : [`${prefix}${String(value)}`];
  return error.response?.data ? flatten(error.response.data).join(" ") : error.message || "Unable to update settings.";
};

export default function InterviewEmailSettings({ embedded = false }) {
  const [companyId, setCompanyId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState({ updatedByName: null, updatedAt: null });
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    document.title = "ATS - Interview Email Settings";
  }, []);
  useEffect(() => {
    if (!companyId) return;
    let active = true;
    setLoading(true);
    setSettings(null);
    axios.get("/api/v1/interview-email-settings/", { params: { company_id: companyId } })
      .then(({ data }) => {
        if (!active) return;
        const serverSettings = data?.data || data;
        setSettings(readInterviewEmailSettings(serverSettings));
        setAudit({ updatedByName: serverSettings?.updated_by_name ?? null, updatedAt: serverSettings?.updated_at ?? null });
        setLoadedCompanyId(companyId);
      })
      .catch(failure => {
        if (active) Swal.fire({ icon: "error", title: "Unable to Load Settings", text: errorText(failure), confirmButtonText: "OK" });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [companyId]);
  const ready = companyId && settings && loadedCompanyId === companyId && !loading && !saving;
  const save = async () => {
    if (!ready) return;
    setSaving(true);
    let saved = false;
    try {
      await axios.patch("/api/v1/interview-email-settings/", { ...settings, company_id: companyId }, { params: { company_id: companyId } });
      saved = true;
      const { data } = await axios.get("/api/v1/interview-email-settings/", { params: { company_id: companyId } });
      const serverSettings = data?.data || data;
      setSettings(readInterviewEmailSettings(serverSettings));
      setAudit({ updatedByName: serverSettings?.updated_by_name ?? null, updatedAt: serverSettings?.updated_at ?? null });
      await Swal.fire({ icon: "success", title: "Settings Saved", text: "Interview email preferences have been updated.", confirmButtonText: "OK" });
    } catch (failure) {
      if (saved) { setSettings(null); setLoadedCompanyId(null); }
      await Swal.fire({
        icon: saved ? "warning" : "error",
        title: saved ? "Unable to Refresh Settings" : "Unable to Save Settings",
        text: `${saved ? "Settings were saved, but refreshing them failed. " : ""}${errorText(failure)}`,
        confirmButtonText: "OK",
      });
    } finally { setSaving(false); }
  };
  const formattedUpdatedAt = audit.updatedAt && moment(audit.updatedAt).isValid()
    ? moment(audit.updatedAt).format("MMM DD, YYYY, h:mm A")
    : "Not available";
  return (
    <div className={embedded ? "" : "page-content"}>
    <Container fluid className="p-3">
      <h4 className="mb-2">Interview Email Settings</h4>
      <p className="text-muted">Manage interview email recipients for each company. Turning off a group skips its emails without preventing interview creation.</p>
      <>
        <div style={{ maxWidth: 520, marginBottom: 24 }}>
          <label className="d-block mb-2">Company</label>
          <CompanySelector selectedCompanyId={companyId} disabled={saving} onCompanyChange={company => setCompanyId(company?.company_id || company?.id || null)} />
        </div>
        {loading && <p role="status">Loading settings...</p>}
        <div style={{ maxWidth: 880 }}>
          {interviewEmailFields.map(({ key, label, description }) => <div key={key} className="interview-email-setting-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, padding: "20px 0", borderBottom: "1px solid #e2e8f0" }}>
            <div className="interview-email-setting-copy"><label htmlFor={key} className="fw-semibold mb-1">{label}</label><p className="text-muted mb-0">{description}</p></div>
            <div className="d-flex align-items-center gap-3" style={{ flexShrink: 0 }}>
              <span>{settings && loadedCompanyId === companyId ? (settings[key] ? "ON" : "OFF") : "--"}</span>
              <InputSwitch inputId={key} checked={settings?.[key] === true} disabled={!ready} onChange={event => setSettings(previous => ({ ...previous, [key]: event.value }))} />
            </div>
            {key === "interview_recording_attachment_enabled" && <div className="interview-email-audit" aria-label="Recording setting audit">
              <div>Last updated by: <strong>{audit.updatedByName || "Not available"}</strong></div>
              <div>Last updated: <strong>{formattedUpdatedAt}</strong></div>
            </div>}
          </div>)}
          <Button color="primary" className="mt-4 d-inline-flex align-items-center gap-2" disabled={!ready} onClick={save}>
            <Save size={16} aria-hidden="true" />{saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </>
    </Container>
    </div>
  );
}
